import { ReactNode, createContext, useContext, useCallback, useEffect, useRef, useState } from 'react'
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context'
import { WebStorageStateStore, User } from 'oidc-client-ts'
import { config, validateConfig } from '../config'
import { ApiRequestError, customFetch } from '../api/client'
import { identifyAnalyticsUser, resetAnalytics } from '../lib/analytics'
import { createReturnRefresher } from '../lib/membershipRefresh'
import { claimReauthAttempt, setSessionExpiredHandler } from '../lib/sessionExpiry'
import { AuthReturnState, currentReturnPath, returnPathFromState } from '../lib/authReturn'

interface GroupInfo {
  id: string
  name: string
  slug: string
}

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null | undefined
  email: string | null
  groups: GroupInfo[]
  teamCheckComplete: boolean
  alphaBlocked: boolean
  platformBlocked: boolean
  getAccessToken: () => string | null
  login: () => void
  logout: () => void
  signinSilent: () => Promise<void>
}

interface EnsureTeamResponse {
  created: boolean
  team: {
    id: string
    name: string
    slug: string
  } | null
  requires_token_refresh: boolean
  alpha_blocked: boolean
}

function parseGroupsFromToken(user: User | null | undefined): GroupInfo[] {
  const groupsClaim = user?.profile.groups as string[] | undefined
  if (!groupsClaim) return []

  // Groups in JWT are Keycloak group UUIDs
  // For now, we use the UUID as id, name, and slug
  // The backend team management API will provide full group details when needed
  return groupsClaim.map(id => ({
    id,
    name: id,
    slug: id,
  }))
}

const AuthContext = createContext<AuthContextValue | null>(null)

// The api client reads the token outside React. AuthContextBridge publishes
// it while rendering, not from an effect: child effects run before the
// provider's, so an effect that fetches when the token renews (TeamContext's
// /me refetch, TRA-1576) would otherwise send the previous token. Unmounting
// the provider clears it.
let renderedAccessToken: string | null = null
let currentAccessToken: string | null = null

function publishAccessToken(token: string | null): void {
  renderedAccessToken = token
  currentAccessToken = token
}

// Withdraws the token only when the provider unmounts, never on renewal: an
// effect cleanup would run before child effects. StrictMode runs the cleanup
// and then the body again on mount, so the body republishes.
function useAccessTokenWithdrawnOnUnmount(): void {
  useEffect(() => {
    currentAccessToken = renderedAccessToken
    return () => { currentAccessToken = null }
  }, [])
}

// eslint-disable-next-line react-refresh/only-export-components -- Intentional: utility function for API client
export function getAccessToken(): string | null {
  return currentAccessToken
}

function AuthContextBridge({ children }: { children: ReactNode }): React.JSX.Element {
  const auth = useOidcAuth()
  const [teamCheckComplete, setTeamCheckComplete] = useState(false)
  const [alphaBlocked, setAlphaBlocked] = useState(false)
  const [platformBlocked, setPlatformBlocked] = useState(false)

  publishAccessToken(auth.user?.access_token ?? null)
  useAccessTokenWithdrawnOnUnmount()

  // A session that expired while the tab sat idle surfaces only as a 401:
  // react-oidc-context keeps isAuthenticated true when silent renew fails, so
  // pages would each render their own load error. Dropping the OIDC user
  // flips isAuthenticated false and AuthGuard takes over, which is the one
  // login path this app has. Keycloak signs the user straight back in when
  // its own session is still alive.
  const expireSession = useCallback(() => {
    if (!auth.isAuthenticated) return
    if (!claimReauthAttempt(Date.now())) return
    void auth.removeUser()
  }, [auth])

  useEffect(() => {
    setSessionExpiredHandler(expireSession)
    return () => { setSessionExpiredHandler(null) }
  }, [expireSession])

  // Tie analytics events to the signed-in user (no-op when analytics is off)
  const email = auth.user?.profile.email
  useEffect(() => {
    if (email) {
      identifyAnalyticsUser(email)
    }
  }, [email])

  // Team check: Ensure user has at least one team after authentication
  useEffect(() => {
    const checkTeam = async (): Promise<void> => {
      // Only run if authenticated and not yet checked
      if (!auth.isAuthenticated || teamCheckComplete || auth.isLoading) {
        return
      }

      try {
        const response = await customFetch<EnsureTeamResponse>(
          '/api/auth/ensure-team',
          { method: 'POST' }
        )

        setAlphaBlocked(response.alpha_blocked)

        // If a team was created, we need to refresh the token to get updated groups claim
        if (response.requires_token_refresh) {
          await auth.signinSilent()
        }

        setTeamCheckComplete(true)
      } catch (error) {
        if (error instanceof ApiRequestError && error.code === 'platform_access_denied') {
          setPlatformBlocked(true)
          setTeamCheckComplete(true)
          return
        }
        // Set complete even on error to avoid blocking the app
        // User can still access the app, they just might not have a team yet
        console.error('Team check failed:', error)
        setTeamCheckComplete(true)
      }
    }

    void checkTeam()
  }, [auth.isAuthenticated, auth.isLoading, teamCheckComplete, auth])

  const signinSilent = useCallback(async (): Promise<void> => {
    await auth.signinSilent()
  }, [auth])

  // A team created or joined while this tab sat in the background lives only
  // in a new token (TRA-1576). Renew it quietly when the user comes back, at
  // most once a minute; TeamContext reloads the team list when the groups
  // claim changes. One pair of listeners for the life of the provider, so
  // the handler reads the latest auth through a ref.
  const latestAuth = useRef(auth)
  useEffect(() => {
    latestAuth.current = auth
  }, [auth])
  useEffect(() => {
    const refresher = createReturnRefresher({
      isSignedIn: () => latestAuth.current.isAuthenticated,
      refresh: () => latestAuth.current.signinSilent().then(() => undefined),
      startedAt: Date.now(),
    })
    const onReturn = (): void => {
      if (document.visibilityState !== 'visible') return
      refresher.onReturn(Date.now())
    }
    document.addEventListener('visibilitychange', onReturn)
    window.addEventListener('focus', onReturn)
    return () => {
      document.removeEventListener('visibilitychange', onReturn)
      window.removeEventListener('focus', onReturn)
    }
  }, [])

  // Remember the page the user asked for, so onSigninCallback can bring them
  // back to it after Keycloak (TRA-1361).
  const login = useCallback(() => {
    const state: AuthReturnState = { returnTo: currentReturnPath(window.location) }
    void auth.signinRedirect({ state })
  }, [auth])
  const logout = useCallback(() => {
    // Get the id_token before clearing state (needed to skip confirmation)
    const idToken = auth.user?.id_token

    resetAnalytics()

    // Clear all OIDC state from localStorage
    const keysToRemove = Object.keys(localStorage).filter(key => key.startsWith('oidc.'))
    keysToRemove.forEach(key => {
      localStorage.removeItem(key)
    })

    // Redirect to Keycloak logout - include id_token_hint to skip confirmation
    let logoutUrl = `${config.KEYCLOAK_AUTHORITY}/protocol/openid-connect/logout?client_id=${config.KEYCLOAK_CLIENT_ID}&post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`
    if (idToken) {
      logoutUrl += `&id_token_hint=${idToken}`
    }
    window.location.href = logoutUrl
  }, [auth.user?.id_token])

  const groups = parseGroupsFromToken(auth.user)

  const value: AuthContextValue = {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user,
    email: auth.user?.profile.email ?? null,
    groups,
    teamCheckComplete,
    alphaBlocked,
    platformBlocked,
    getAccessToken,
    login,
    logout,
    signinSilent,
  }

  // Don't render children until team check is complete (or user is not authenticated)
  const shouldRenderChildren = !auth.isAuthenticated || teamCheckComplete

  return (
    <AuthContext.Provider value={value}>
      {shouldRenderChildren ? children : null}
    </AuthContext.Provider>
  )
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  // Validate config on first render (throws if missing required values)
  validateConfig()

  const oidcConfig: Parameters<typeof OidcAuthProvider>[0] = {
    authority: config.KEYCLOAK_AUTHORITY,
    client_id: config.KEYCLOAK_CLIENT_ID,
    redirect_uri: window.location.origin,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'openid profile email groups',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    onSigninCallback: (user) => {
      // Drop the code from the URL and go back to the page the user opened
      // before sign-in (TRA-1361). Without a saved page, stay where we are.
      const returnTo = returnPathFromState(user?.state, window.location.origin, window.location.pathname)
      window.history.replaceState(window.history.state, document.title, returnTo)
      // BrowserRouter sits inside this provider and only re-reads the address
      // on popstate, so tell it the address changed.
      window.dispatchEvent(new PopStateEvent('popstate'))
    },
  }

  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthContextBridge>{children}</AuthContextBridge>
    </OidcAuthProvider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- Intentional: custom hook for auth context
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
